import React from 'react';
import { motion } from 'framer-motion';
import { useLanguageStore } from '../../store/languageStore';
import { useTranslation } from 'react-i18next';

interface PageHeaderProps {
  title: string;
  subtitle?: string | React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  className = '',
}) => {
  const { isRTL, language } = useLanguageStore();
  const { t: _t } = useTranslation();

  return (
    <motion.div
      className={`d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 ${className}`}
      style={{ gap: '1.5rem' }}
      dir={isRTL ? 'rtl' : 'ltr'}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex-grow-1" style={{ textAlign: isRTL ? 'right' : 'left' }}>
        <motion.h1
          className="mb-0 fw-bold gradient-text"
          style={{
            fontSize: '2rem',
            color: 'var(--color-text-primary)',
            fontFamily: language === 'ar' ? 'var(--font-family-arabic)' : 'var(--font-family-base)',
            letterSpacing: language === 'ar' ? '0' : '0.01em',
            textAlign: isRTL ? 'right' : 'left',
          }}
          initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.div
            className="mt-2 mb-0"
            style={{
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)',
              fontFamily: language === 'ar' ? 'var(--font-family-arabic)' : 'var(--font-family-base)',
              textAlign: isRTL ? 'right' : 'left',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {subtitle}
          </motion.div>
        )}
      </div>
      {actions && (
        <motion.div
          className="d-flex align-items-center flex-wrap"
          style={{ gap: '0.75rem' }}
          initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          {actions}
        </motion.div>
      )}
    </motion.div>
  );
};
